import React, { useState, useEffect } from 'react'

import { FadeSwap, LayoutContainer, ProductReviewCard, Button } from '@pb/components'
import {
  trackEvent,
  urlHasParam,
  getUrlParam,
  currentBrand,
  isCosBrand,
  getLabelFromStorage,
  LoadingStates,
  REACT_BASE_ROUTES,
  getLocale,
} from '@pb/utils'

import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'

import { operationFetchUser } from 'App/duck/operations'
import appSelectors from 'App/duck/selectors'

import BackNavigationLayout from './components/BackNavigationLayout'
import ErrorContainer from './components/ErrorContainer'
import Header from './components/Header'
import Loader from './components/Loader'
import ProductDetail from './components/ProductDetail'
import { LIMIT_DAYS_TO_REVIEW } from './constants'
import { operationFetchUnreviewedProductsData, operationCreateReview } from './duck/operations'
import selectors from './duck/selectors'
import { PurchasedProducts } from './duck/types'
import { UnreviewedProductsProps, ReviewData } from './types'
import styles from './UnreviewedProducts.scss'

const UnreviewedProductsComponent: React.FC<UnreviewedProductsProps> = () => {
  const user = useSelector(appSelectors.getUser)
  const dispatch = useDispatch()
  const fetchUser = (): any => dispatch(operationFetchUser())
  const unreviewProductsData = useSelector(selectors.getUnreviewedProducts)
  const isLoading = useSelector(selectors.getUnreviewedProductsLoading)
  const loadingState = useSelector(selectors.getUnreviewedProductsLoadingState)
  const fetchUnreviewedData = (): any =>
    dispatch(
      operationFetchUnreviewedProductsData(
        user.businessPartnerId,
        window.app?.options?.turnTo?.unreviewedPurchases,
      ),
    )
  const isCreateReviewSubmmited = useSelector(selectors.getCreateReviewStatus)
  const headerStatus = useSelector(selectors.getHeaderStatus)
  const containerClass = `${currentBrand}Container`
  const [visibleProducts, setVisibleProducts] = useState(3)
  const [showDetail, setShowDetail] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<PurchasedProducts>()
  const [selectedRate, setSelectedRate] = useState(0)
  const [showErrorLayout, setShowErrorLayout] = useState(false)
  const [errorLayoutStatus, setErrorLayoutStatus] = useState('server')

  if (urlHasParam('alreadyReviewed')) {
    if (getUrlParam('alreadyReviewed') === 'true' && !showErrorLayout) {
      setErrorLayoutStatus('alreadyReviewed')
      setShowErrorLayout(true)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [])

  useEffect(() => {
    // check if we fetched the user info and have the endpoint
    if (user?.businessPartnerId && window.app?.options?.turnTo?.unreviewedPurchases) {
      fetchUnreviewedData()
    }
  }, [user.businessPartnerId])

  const isItNewPurchase = (orderDate: string, days: number): boolean => {
    const firstDate = new Date(orderDate)
    const today = new Date()
    const time = days * 60 * 60 * 24 * 1000
    if (today.getTime() - firstDate.getTime() > time) {
      return false
    }

    return true
  }

  const handleRatingProduct = (sku: string, rating: number): void => {
    const selectedItem = unreviewProductsData?.unreviewedProducts?.filter(
      (x) => x.catalogItem.sku === sku,
    )[0]

    if (selectedItem) {
      setSelectedProduct(selectedItem)

      const hasTimeToReview = isItNewPurchase(selectedItem.orderDate, LIMIT_DAYS_TO_REVIEW)
      if (hasTimeToReview) {
        setShowDetail(true)
        setSelectedRate(rating)
      } else {
        setShowErrorLayout(true)
        setErrorLayoutStatus('old')
      }
      window.scrollTo(0, 0)
    }
  }

  const handleSubmitReview = async (data: ReviewData): Promise<void> => {
    const createReviewEndpoint = window.app?.options?.turnTo?.createReview
    if (data && createReviewEndpoint) {
      dispatch(operationCreateReview(data, createReviewEndpoint))

      // Send trackable event
      trackEvent({
        event_type: 'RATING_SEND_REVIEW',
        event_id: 'Send review',
        event_category: 'Rating and review',
        event_label: data.catalogItems[0].sku,
      })

      window.scrollTo(0, 0)
    }
  }

  const handleBackToReviewClicked = (): void => {
    // remove params when we are coming back from already reviewed error page
    if (urlHasParam('alreadyReviewed') && getUrlParam('alreadyReviewed') && showErrorLayout) {
      window.history.replaceState(null, '', window.location.pathname)
    }

    if (isCosBrand && !showDetail) {
      const locale = getLocale()
      window.location.href = `/${locale}/${REACT_BASE_ROUTES.AccountCos.path}`
    } else {
      fetchUnreviewedData()
    }

    // hidden the error layout and fetch the data again
    setShowDetail(false)
    setSelectedRate(0)
    setShowErrorLayout(false)
  }

  if (!isLoading && isCreateReviewSubmmited) {
    setShowDetail(false)
    fetchUnreviewedData()
  }

  const handleShowMoreClick = (): void => {
    setVisibleProducts(visibleProducts + 3)
  }

  return (
    <FadeSwap
      flag={loadingState === LoadingStates.Loading || loadingState === LoadingStates.NotLoaded}
      placeholder={<Loader />}
    >
      <BackNavigationLayout
        status={isCosBrand ? true : showDetail}
        handleBackButtonClick={handleBackToReviewClicked}
      />
      <LayoutContainer
        Tag="main"
        id="main-content"
        large={showErrorLayout}
        className={cn(styles.layout, styles[containerClass])}
      >
        {!showErrorLayout ? (
          <>
            <Header
              status={headerStatus}
              isDetailPage={showDetail}
              productsTotal={unreviewProductsData.totalNumberOfUnreviewedProducts}
            />
            {!showDetail && (
              <>
                {unreviewProductsData?.unreviewedProducts
                  ?.slice(
                    0,
                    isCosBrand
                      ? visibleProducts
                      : unreviewProductsData?.totalNumberOfUnreviewedProducts,
                  )
                  .map((item) => {
                    return (
                      <ProductReviewCard
                        key={item.catalogItem.sku}
                        catalogItem={item.catalogItem}
                        handleSelectProductToReview={handleRatingProduct}
                        disabled={!isItNewPurchase(item.orderDate, LIMIT_DAYS_TO_REVIEW)}
                      />
                    )
                  })}
                {isCosBrand &&
                  visibleProducts < unreviewProductsData?.totalNumberOfUnreviewedProducts && (
                    <Button size="large" fullWidth secondary onClick={handleShowMoreClick}>
                      {getLabelFromStorage('profile.unreviewedproducts.review.page.show.more')}
                    </Button>
                  )}
              </>
            )}

            {showDetail && selectedProduct && selectedRate && (
              <ProductDetail
                rating={selectedRate}
                data={selectedProduct}
                handleSubmitReviewDataToParent={handleSubmitReview}
              />
            )}
          </>
        ) : (
          <ErrorContainer
            status={errorLayoutStatus}
            handleBackToReview={handleBackToReviewClicked}
          />
        )}
      </LayoutContainer>
    </FadeSwap>
  )
}

export default UnreviewedProductsComponent
